import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Eye, Trash2, FileImage, FileVideo, AlertCircle, FolderOpen } from 'lucide-react';

import type { Job, JobStatus } from '@/types/forensics';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useJobList } from '@/hooks/useJobList';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusBadge({ status }: { status: JobStatus }) {
  const config: Record<JobStatus, { label: string; className: string; spinning?: boolean }> = {
    complete: { label: 'Complete', className: 'bg-green-100 text-green-800' },
    partial_complete: { label: 'Partial', className: 'bg-yellow-100 text-yellow-800' },
    running: { label: 'Running', className: 'bg-blue-100 text-blue-800', spinning: true },
    preprocessing: { label: 'Preprocessing', className: 'bg-blue-100 text-blue-800', spinning: true },
    aggregating: { label: 'Aggregating', className: 'bg-blue-100 text-blue-800', spinning: true },
    queued: { label: 'Queued', className: 'bg-gray-100 text-gray-600' },
    failed: { label: 'Failed', className: 'bg-red-100 text-red-800' },
    canceled: { label: 'Canceled', className: 'bg-gray-100 text-gray-600' },
  };

  const { label, className, spinning } = config[status] ?? config.queued;

  return (
    <Badge className={className}>
      {spinning && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
      {label}
    </Badge>
  );
}

function RiskBadge({ score, band }: { score?: number; band?: string }) {
  if (score === undefined || band === undefined) {
    return <span className="text-sm text-muted-foreground">--</span>;
  }

  const colorMap: Record<string, string> = {
    low: 'bg-green-100 text-green-800',
    moderate: 'bg-orange-100 text-orange-800',
    high: 'bg-red-100 text-red-800',
  };

  return (
    <Badge className={colorMap[band] ?? 'bg-gray-100 text-gray-800'}>
      {score}% {band}
    </Badge>
  );
}

interface JobRowProps {
  job: Job;
  onView: (jobId: string) => void;
  onDelete: (jobId: string) => void;
}

function JobRow({ job, onView, onDelete }: JobRowProps) {
  const filename = job.mediaAsset?.filename ?? 'Unknown file';
  const mediaType = job.mediaAsset?.mediaType ?? 'image';

  return (
    <div className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex-shrink-0">
        {mediaType === 'video' ? (
          <FileVideo className="h-5 w-5 text-muted-foreground" />
        ) : (
          <FileImage className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{filename}</p>
        <p className="text-xs text-muted-foreground">{formatDate(job.createdAt)}</p>
      </div>

      <div className="flex-shrink-0">
        <StatusBadge status={job.status} />
      </div>

      <div className="flex-shrink-0 min-w-[100px] text-center">
        <RiskBadge score={job.riskScore} band={job.riskBand} />
      </div>

      <div className="flex-shrink-0 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onView(job.id)}>
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
        <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => onDelete(job.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { jobs, loading, error, deleteJob } = useJobList();

  const sortedJobs = [...jobs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const handleView = (jobId: string) => {
    navigate(`/analyze/${jobId}`);
  };

  const handleDelete = async (jobId: string) => {
    await deleteJob(jobId);
  };

  return (
    <div className="max-w-5xl mx-auto py-12 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">View and manage your forensic analyses</p>
        </div>
        <Button onClick={() => navigate('/upload')}>
          <Plus className="h-4 w-4 mr-2" />
          New Analysis
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && !loading && (
        <Card>
          <CardContent className="py-10 text-center">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && !error && sortedJobs.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">No analyses yet</h2>
            <p className="text-sm text-muted-foreground mb-6">Upload an image or video to start your first forensic analysis.</p>
            <Button onClick={() => navigate('/upload')}>
              <Plus className="h-4 w-4 mr-2" />
              New Analysis
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && !error && sortedJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Analyses ({sortedJobs.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sortedJobs.map((job) => (
              <JobRow key={job.id} job={job} onView={handleView} onDelete={handleDelete} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
